import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Markdown, Modal, Typography } from '@scale/scaleui';
import { TOAST_CONTAINER_PROPS } from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import ihjGif from './ihj.gif';

type QueryType = 'query' | 'mutation' | 'unknown';

/**
 * Display an error toast with debug info.
 *
 * @param errorId Unique identifier for the error, e.g. to search for it in logs.
 * @param userMessage Operator-facing error message.
 * @param serverMessage Developer-facing server-defined error message.
 * @param clientMessage Developer-facing client-defined error message.
 * @param statusCode HTTP status code.
 * @param args Arguments object passed to ReactQuery function that threw the error.
 * @param context Additional context from ReactQuery.
 */
export function errorToast(
  errorId: string,
  queryType: QueryType,
  userMessage: string,
  serverMessage?: string,
  clientMessage?: string,
  statusCode?: string,
  url?: string,
  args?: any,
  context?: any,
) {
  toast.error(
    <ToastContent
      errorId={errorId}
      queryType={queryType}
      userMessage={userMessage}
      serverMessage={serverMessage}
      clientMessage={clientMessage}
      statusCode={statusCode}
      url={url}
      args={args}
      context={context}
    />,
    {
      closeButton: CloseButton, // Default close button shrinks depending on toast content, so we use a custom one.
      autoClose: 7000,
      closeOnClick: false,
    },
  );
  toast.clearWaitingQueue({ containerId: TOAST_CONTAINER_PROPS.ID }); // Prevents multiple toasts from stacking up.
}

type ToastContentProps = {
  errorId: string;
  queryType: QueryType;
  userMessage: string;
  serverMessage?: string;
  clientMessage?: string;
  statusCode?: string;
  url?: string;
  args?: any;
  context?: any;
};

function safeJsonStringify(obj: any): string {
  try {
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    return 'Error stringifying object';
  }
}

const statusCodeMap = {
  '400': {
    error: 'Bad Request',
    description: 'The request was invalid or cannot be otherwise served.',
  },
  '401': {
    error: 'Unauthorized',
    description: 'Authentication is required but has not been provided or is invalid',
  },
  '403': {
    error: 'Forbidden',
    description: 'The client is authenticated but does not have permission to access the resource.',
  },
  '404': {
    error: 'Not Found',
    description: 'The requested resource could not be found on the server',
  },
  '405': {
    error: 'Method Not Allowed',
    description: 'The HTTP method used in the request is not supported for the requested resource',
  },
  '406': {
    error: 'Not Acceptable',
    description:
      'The requested resource is only capable of generating content not acceptable according to the Accept headers sent in the request.',
  },
  '407': {
    error: 'Proxy Authentication Required',
    description: 'The client must first authenticate itself with the proxy.',
  },
  '408': {
    error: 'Request Timeout',
    description: "The server timed out waiting for the client's request.",
  },
  '409': {
    error: 'Conflict',
    description: 'The request conflicts with the current state of the resource.',
  },
  '413': {
    error: 'Payload Too Large',
    description: 'The request is larger than the server is willing or able to process.',
  },
  '414': {
    error: 'URI Too Long',
    description: 'The URI provided was too long for the server to process.',
  },
  '429': {
    error: 'Too Many Requests',
    description: 'The user has sent too many requests in a given amount of time.',
  },
  '500': {
    error: 'Internal Server Error',
    description:
      'The server encountered an unexpected condition that prevented it from fulfilling the request.',
  },
  '501': {
    error: 'Not Implemented',
    description: 'The server does not support the functionality required to fulfill the request.',
  },
  '502': {
    error: 'Bad Gateway',
    description:
      'The server received an invalid response from an upstream server while acting as a gateway or proxy.',
  },
  '503': {
    error: 'Service Unavailable',
    description: '.The server is currently unavailable.',
  },
  '504': {
    error: 'Gateway Timeout',
    description:
      'The server did not receive a timely response from an upstream server while acting as a gateway or proxy.',
  },
  '505': {
    error: 'HTTP Version Not Supported',
    description: 'The server does not support the HTTP protocol version used in the request.',
  },
};

const ToastContent: FC<ToastContentProps> = ({
  errorId,
  queryType,
  userMessage,
  serverMessage,
  clientMessage,
  statusCode,
  url,
  args,
  context,
}) => {
  const [isDebugModalOpen, setIsDebugModalOpen] = useState<boolean>(false);
  const [ihj, setIhj] = useState<boolean>(false);

  const title = `Debug info for ${queryType} error`;
  const datadogUrl = `https://app.datadoghq.com/logs?query=%40logId%3A${errorId}`;
  const argsJson = '```json\n' + safeJsonStringify(args) + '\n```';
  const contextJson = '```json\n' + safeJsonStringify(context) + '\n```';
  return (
    <div className="scaleui scale-theme scale-tailwind flex flex-col px-1">
      {userMessage}

      <div>
        <Button
          className="mt-4 w-fit"
          variant="neutral"
          onClick={() => setIsDebugModalOpen(!isDebugModalOpen)}
        >
          Open debug info
        </Button>
      </div>
      <Modal
        open={isDebugModalOpen}
        onClose={() => setIsDebugModalOpen(false)}
        title={
          <div className="flex justify-center gap-2">
            {ihj && (
              <div style={{ display: 'inline-block', width: 25, height: 25 }}>
                <img src={ihjGif.src} style={{ maxWidth: '100%', maxHeight: '100%' }} />
              </div>
            )}
            {title}
            {ihj && (
              <div style={{ display: 'inline-block', width: 25, height: 25 }}>
                <img src={ihjGif.src} style={{ maxWidth: '100%', maxHeight: '100%' }} />
              </div>
            )}
          </div>
        }
      >
        <div className="flex flex-col">
          <div className="row flex gap-2">
            <Button
              variant="neutral"
              icon={{ type: 'copy' }}
              onClick={() => {
                navigator.clipboard.writeText(errorId);
                setIhj(true);
              }}
            >
              Copy Datadog logId
            </Button>
            <Button
              variant="neutral"
              icon={{ type: 'external-link-alt' }}
              onClick={() => {
                window.open(datadogUrl, '_blank');
                setIhj(true);
              }}
            >
              Open in Datadog
            </Button>
          </div>
          <div className="row">
            <Typography.Subtitle3>User message</Typography.Subtitle3>
            <Typography.Paragraph3>
              {userMessage ?? 'No user message provided.'}
            </Typography.Paragraph3>
          </div>
          <div className="row">
            <Typography.Subtitle3>Client debug message</Typography.Subtitle3>
            <Typography.Paragraph3>
              {clientMessage ?? 'No client debug message provided.'}
            </Typography.Paragraph3>
          </div>
          <div className="row">
            <Typography.Subtitle3>Server debug message</Typography.Subtitle3>
            <Typography.Paragraph3 className="whitespace-pre-line">
              {serverMessage
                ? JSON.stringify(serverMessage, null, 2).replace(/\\n/g, '\n') // Even though serverMessage is a string, if it looks like an object React might try think it's an object and throw an error.
                : 'No server debug message provided.'}
            </Typography.Paragraph3>
          </div>
          <div className="row">
            <Typography.Subtitle3>Status code</Typography.Subtitle3>
            <div className="flex flex-col">
              <Typography.Paragraph3>
                {statusCode
                  ? statusCode +
                    ' ' +
                    statusCodeMap[statusCode as keyof typeof statusCodeMap]?.error
                  : 'No status code provided.'}
              </Typography.Paragraph3>
              <Typography.Paragraph3>
                {statusCode
                  ? ' ' + statusCodeMap[statusCode as keyof typeof statusCodeMap]?.description
                  : ''}
              </Typography.Paragraph3>
            </div>
          </div>
          <div className="row">
            <Typography.Subtitle3>URL</Typography.Subtitle3>
            <Typography.Paragraph3>{url ?? 'No URL provided.'}</Typography.Paragraph3>
          </div>
          <div className="row">
            <Typography.Subtitle3>
              {queryType === 'query' ? 'Query key' : 'Mutation args'}
            </Typography.Subtitle3>
            <Markdown className="mt-2" markdown={argsJson} />
          </div>
          <div>
            <Typography.Subtitle3>Additional context</Typography.Subtitle3>
            {context ? (
              <Markdown className="mt-2" markdown={contextJson} />
            ) : (
              <Typography.Paragraph3>No additional context provided.</Typography.Paragraph3>
            )}
          </div>
        </div>
      </Modal>

      <style jsx>{`
        .row {
          width: 500px;
          margin-bottom: 20px;
        }
      `}</style>
    </div>
  );
};

type CloseButtonProps = {
  closeToast: (e: any) => void;
};

const CloseButton: FC<CloseButtonProps> = ({ closeToast }) => (
  <FontAwesomeIcon
    icon={['fas', 'times']}
    className="scale-tailwind cursor-pointer"
    onClick={e => closeToast(e)}
    size="lg"
  />
);
